import { useState } from 'react';
import StatCards from '../components/encryption/StatCards';
import DeviceTable from '../components/encryption/DeviceTable';
import { Box, Container, Modal } from '@mui/material';
import { Select, Button, TextInput } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes, faWindows, faLinux } from '@fortawesome/free-solid-svg-icons';
import { faWindows as faWindowsBrand, faLinux as faLinuxBrand } from '@fortawesome/free-brands-svg-icons';
import deviceOptions from '../data/deviceOptions.json';

function Encryption() {
  // State for devices
  const [devices, setDevices] = useState([
    {
      device: 'LAPTOP-123',
      department: 'Finance Dept',
      os: 'Windows 10',
      disks: '2 Disks',
      diskCount: 2,
      status: 'Ready',
      model: 'Dell XPS 13',
      location: 'HQ - First Floor',
      ipAddress: '192.168.1.101',
      macAddress: '00:1A:2B:3C:4D:5F',
    },
    {
      device: 'DESKTOP-AX12',
      department: 'R&D Lab',
      os: 'Windows 11',
      disks: '3 Disks',
      diskCount: 3,
      status: 'In Progress',
      model: 'HP EliteDesk',
      location: 'Lab 2',
      ipAddress: '192.168.1.102',
      macAddress: '00:1A:2B:3C:4D:60',
    },
    {
      device: 'LAPTOP-T567',
      department: 'HR Division',
      os: 'Linux',
      disks: '1 Disk',
      diskCount: 1,
      status: 'Completed',
      model: 'Lenovo ThinkPad',
      location: 'HR Office',
      ipAddress: '192.168.1.103',
      macAddress: '00:1A:2B:3C:4D:61',
    },
    {
      device: 'SERVER-01',
      department: 'IT Department',
      os: 'Linux',
      disks: '4 Disks',
      diskCount: 4,
      status: 'Failed',
      model: 'Dell PowerEdge',
      location: 'Server Room',
      ipAddress: '192.168.1.104',
      macAddress: '00:1A:2B:3C:4D:62',
    },
    {
      device: 'DEVICE-ZZ-2',
      department: 'Remote Team',
      os: 'Windows 11',
      disks: '2 Disks',
      diskCount: 2,
      status: 'In Progress',
      model: 'Asus ZenBook',
      location: 'Remote',
      ipAddress: '192.168.1.105',
      macAddress: '00:1A:2B:3C:4D:63',
    },
  ]);

  // State for modal
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState({
    deviceName: '',
    model: '',
    department: '',
    location: '',
    os: '',
    ipAddress: '',
    macAddress: '',
    diskCount: 1,
  });
  const [osFetchStatus, setOsFetchStatus] = useState('');
  const [fetchingOs, setFetchingOs] = useState(false);

  // State for filters
  const [departmentFilter, setDepartmentFilter] = useState('All Departments');
  const [osFilter, setOsFilter] = useState('All OS Types');
  const [statusFilter, setStatusFilter] = useState('All Status');

  // Extract unique filter options from devices
  const departments = ['All Departments', ...new Set(devices.map(item => item.department))];
  const osTypes = ['All OS Types', ...new Set(devices.map(item => item.os))];
  const statuses = ['All Status', ...new Set(devices.map(item => item.status))];

  // Filter the data based on selected filters
  const filteredData = devices.filter(item => {
    const matchesDepartment = departmentFilter === 'All Departments' || item.department === departmentFilter;
    const matchesOs = osFilter === 'All OS Types' || item.os === osFilter;
    const matchesStatus = statusFilter === 'All Status' || item.status === statusFilter;
    return matchesDepartment && matchesOs && matchesStatus;
  });

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Simulate OS fetching and disk count
  const handleFetchOs = () => {
    setFetchingOs(true);
    setOsFetchStatus('');
    setTimeout(() => {
      const success = Math.random() > 0.3;
      if (success) {
        const osList = deviceOptions.osOptions;
        const randomOs = osList[Math.floor(Math.random() * osList.length)];
        const diskCount = Math.floor(Math.random() * 3) + 1;
        setFormData(prev => ({
          ...prev,
          os: randomOs.name,
          diskCount: diskCount,
        }));
        setOsFetchStatus('OS Fetched successfully');
      } else {
        setOsFetchStatus('OS Fetching failed');
      }
      setFetchingOs(false);
    }, 1000);
  };

  // Handle form submission
  const handleAddDevice = () => {
    const newDevice = {
      device: formData.deviceName || 'DEVICE-UNKNOWN',
      department: formData.department || 'Unassigned',
      os: formData.os || 'Unknown OS',
      disks: `${formData.diskCount} Disk${formData.diskCount !== 1 ? 's' : ''}`,
      diskCount: formData.diskCount,
      status: 'Ready',
      model: formData.model || 'Unknown Model',
      location: formData.location || '--',
      ipAddress: formData.ipAddress || '--',
      macAddress: formData.macAddress || '--',
    };
    setDevices(prev => [...prev, newDevice]);
    setOpenModal(false);
    setFormData({
      deviceName: '',
      model: '',
      department: '',
      location: '',
      os: '',
      ipAddress: '',
      macAddress: '',
      diskCount: 1,
    });
    setOsFetchStatus('');
  };

  // Ripple effect handler
  const handleRippleEffect = (e) => {
    const button = e.currentTarget;
    const ripple = document.createElement('span');
    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius = diameter / 2;

    ripple.style.width = ripple.style.height = `${diameter}px`;
    ripple.style.left = `${e.clientX - button.getBoundingClientRect().left - radius}px`;
    ripple.style.top = `${e.clientY - button.getBoundingClientRect().top - radius}px`;
    ripple.classList.add('export-button-ripple');

    button.appendChild(ripple);

    setTimeout(() => {
      ripple.remove();
    }, 600);
  };

  return (
    <Box display="flex" sx={{ maxWidth: 'none', justifyContent: 'center' }}>
      <Box flexGrow={1} ml={{ sm: 0 }} sx={{ maxWidth: 'none' }}>
        <Container
          className="py-4"
          sx={{
            maxWidth: 'none !important',
            overflowX: 'hidden',
            paddingLeft: 0,
            paddingRight: 0,
          }}
        >
          <div style={{ margin: '0 auto', width: '75.5vw', maxWidth: '75.5vw' }}>
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <h1
                style={{
                  fontFamily: 'Nunito Sans, sans-serif',
                  fontSize: '32px',
                  fontWeight: 700,
                  color: '#202224',
                }}
              >
                Data Encryption
              </h1>
              <Button
                size="sm"
                variant="filled"
                className="export-button"
                styles={{
                  root: {
                    width: '120px',
                    height: '36px',
                    backgroundColor: '#FF7A00',
                    borderRadius: '20px',
                    padding: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
                    '&:hover': {
                      backgroundColor: '#FF7A00',
                    },
                  },
                  label: {
                    fontFamily: 'Nunito Sans, sans-serif',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  },
                }}
                onClick={(e) => {
                  handleRippleEffect(e);
                  setOpenModal(true);
                }}
              >
                <FontAwesomeIcon
                  icon={faPlus}
                  style={{
                    height: '10px',
                    width: '10px',
                    color: '#fff',
                  }}
                />
                <span>Add Devices</span>
              </Button>
            </div>

            {/* Filter Dropdowns */}
            <div className="flex flex-wrap gap-4 md:gap-[30px] mb-6">
              <Select
                value={departmentFilter}
                onChange={setDepartmentFilter}
                data={departments}
                placeholder="All Departments"
                styles={{
                  input: {
                    fontFamily: 'Nunito Sans, sans-serif',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#202224',
                    border: '0.6px solid #202224',
                    backgroundColor: '#FCFDFD',
                    borderRadius: '6px',
                    height: '36px',
                    width: '180px',
                    '&:focus': {
                      borderColor: '#FF6B00',
                      outline: 'none',
                    },
                  },
                  item: {
                    fontFamily: 'Nunito Sans, sans-serif',
                    fontSize: '12px',
                    color: '#202224',
                    '&[data-selected]': {
                      backgroundColor: '#FF6B00',
                      color: '#FFFFFF',
                      '&:hover': {
                        backgroundColor: '#FF6B00',
                        color: '#FFFFFF',
                      },
                    },
                    '&:hover': {
                      backgroundColor: '#FFF3E5',
                    },
                  },
                }}
              />
              <Select
                value={osFilter}
                onChange={setOsFilter}
                data={osTypes}
                placeholder="All OS Types"
                styles={{
                  input: {
                    fontFamily: 'Nunito Sans, sans-serif',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#202224',
                    border: '0.6px solid #202224',
                    backgroundColor: '#FCFDFD',
                    borderRadius: '6px',
                    height: '36px',
                    width: '180px',
                    '&:focus': {
                      borderColor: '#FF6B00',
                      outline: 'none',
                    },
                  },
                  item: {
                    fontFamily: 'Nunito Sans, sans-serif',
                    fontSize: '12px',
                    color: '#202224',
                    '&[data-selected]': {
                      backgroundColor: '#FF6B00',
                      color: '#FFFFFF',
                      '&:hover': {
                        backgroundColor: '#FF6B00',
                        color: '#FFFFFF',
                      },
                    },
                    '&:hover': {
                      backgroundColor: '#FFF3E5',
                    },
                  },
                }}
              />
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                data={statuses}
                placeholder="All Status"
                styles={{
                  input: {
                    fontFamily: 'Nunito Sans, sans-serif',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#202224',
                    border: '0.6px solid #202224',
                    backgroundColor: '#FCFDFD',
                    borderRadius: '6px',
                    height: '36px',
                    width: '180px',
                    '&:focus': {
                      borderColor: '#FF6B00',
                      outline: 'none',
                    },
                  },
                  item: {
                    fontFamily: 'Nunito Sans, sans-serif',
                    fontSize: '12px',
                    color: '#202224',
                    '&[data-selected]': {
                      backgroundColor: '#FF6B00',
                      color: '#FFFFFF',
                      '&:hover': {
                        backgroundColor: '#FF6B00',
                        color: '#FFFFFF',
                      },
                    },
                    '&:hover': {
                      backgroundColor: '#FFF3E5',
                    },
                  },
                }}
              />
            </div>

            {/* Stat Cards */}
            <div>
              <StatCards />
            </div>

            {/* Device Table */}
            <div>
              <DeviceTable data={filteredData} />
            </div>
          </div>

          {/* Add Devices Modal */}
          <Modal
            open={openModal}
            onClose={() => {
              setOpenModal(false);
              setFormData({
                deviceName: '',
                model: '',
                department: '',
                location: '',
                os: '',
                ipAddress: '',
                macAddress: '',
                diskCount: 1,
              });
              setOsFetchStatus('');
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                backgroundColor: '#F7F7F7',
                padding: '20px 30px',
                borderRadius: '8px',
                width: '90vw',
                maxWidth: '900px',
                position: 'relative',
                maxHeight: { xs: '90vh', sm: 'none' },
                overflowY: { xs: 'auto', sm: 'hidden' },
                boxShadow: '6px 6px 6px #00000040',
              }}
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center mb-4">
                <h1
                  style={{
                    fontFamily: 'Nunito Sans, sans-serif',
                    fontSize: '30px',
                    fontWeight: 700,
                    color: '#202224',
                  }}
                >
                  Add New Device
                </h1>
                <FontAwesomeIcon
                  icon={faTimes}
                  style={{
                    height: '17px',
                    width: '17px',
                    color: '#FF7A00',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    setOpenModal(false);
                    setFormData({
                      deviceName: '',
                      model: '',
                      department: '',
                      location: '',
                      os: '',
                      ipAddress: '',
                      macAddress: '',
                      diskCount: 1,
                    });
                    setOsFetchStatus('');
                  }}
                />
              </div>

              {/* Device Information Section */}
              <h2
                style={{
                  fontFamily: 'Nunito Sans, sans-serif',
                  fontSize: { xs: '18px', sm: '20px' },
                  fontWeight: 700,
                  color: '#202224',
                  marginBottom: '16px',
                }}
              >
                Device Information
              </h2>
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                {/* Device Name */}
                <div className="flex flex-col flex-1">
                  <label
                    style={{
                      fontFamily: 'Nunito Sans, sans-serif',
                      fontSize: { xs: '10px', sm: '12px' },
                      fontWeight: 600,
                      color: '#202224',
                      marginBottom: '6px',
                    }}
                  >
                    Device Name*
                  </label>
                  <TextInput
                    value={formData.deviceName}
                    onChange={(e) => handleInputChange('deviceName', e.target.value)}
                    placeholder="e.g., WS-IT-001"
                    styles={{
                      input: {
                        fontFamily: 'Nunito Sans, sans-serif',
                        fontSize: { xs: '10px', sm: '12px' },
                        color: '#595C60',
                        backgroundColor: '#FCFCFC',
                        border: '0.6px solid #595C60',
                        borderRadius: '6px',
                        width: '100%',
                        maxWidth: { xs: '100%', sm: '270px' },
                        height: { xs: '36px', sm: '40px' },
                        '&:focus': {
                          borderColor: '#FF6B00',
                          outline: 'none',
                        },
                      },
                    }}
                  />
                </div>
                {/* Model */}
                <div className="flex flex-col flex-1">
                  <label
                    style={{
                      fontFamily: 'Nunito Sans, sans-serif',
                      fontSize: { xs: '10px', sm: '12px' },
                      fontWeight: 600,
                      color: '#202224',
                      marginBottom: '6px',
                    }}
                  >
                    Model*
                  </label>
                  <TextInput
                    value={formData.model}
                    onChange={(e) => handleInputChange('model', e.target.value)}
                    placeholder="e.g., Dell Latitude 7420"
                    styles={{
                      input: {
                        fontFamily: 'Nunito Sans, sans-serif',
                        fontSize: { xs: '10px', sm: '12px' },
                        color: '#595C60',
                        backgroundColor: '#FCFCFC',
                        border: '0.6px solid #595C60',
                        borderRadius: '6px',
                        width: '100%',
                        maxWidth: { xs: '100%', sm: '270px' },
                        height: { xs: '36px', sm: '40px' },
                        '&:focus': {
                          borderColor: '#FF6B00',
                          outline: 'none',
                        },
                      },
                    }}
                  />
                </div>
                {/* Department */}
                <div className="flex flex-col flex-1">
                  <label
                    style={{
                      fontFamily: 'Nunito Sans, sans-serif',
                      fontSize: { xs: '10px', sm: '12px' },
                      fontWeight: 600,
                      color: '#202224',
                      marginBottom: '6px',
                    }}
                  >
                    Department*
                  </label>
                  <Select
                    value={formData.department}
                    onChange={(value) => handleInputChange('department', value)}
                    data={deviceOptions.departments}
                    placeholder="Select Department"
                    styles={{
                      input: {
                        fontFamily: 'Nunito Sans, sans-serif',
                        fontSize: { xs: '10px', sm: '12px' },
                        color: '#595C60',
                        backgroundColor: '#FCFCFC',
                        border: '0.6px solid #595C60',
                        borderRadius: '6px',
                        width: '100%',
                        maxWidth: { xs: '100%', sm: '270px' },
                        height: { xs: '36px', sm: '40px' },
                        '&:focus': {
                          borderColor: '#FF6B00',
                          outline: 'none',
                        },
                      },
                      item: {
                        fontFamily: 'Nunito Sans, sans-serif',
                        fontSize: { xs: '10px', sm: '12px' },
                        color: '#595C60',
                        '&[data-selected]': {
                          backgroundColor: '#FF6B00',
                          color: '#FFFFFF',
                          '&:hover': {
                            backgroundColor: '#FF6B00',
                            color: '#FFFFFF',
                          },
                        },
                        '&:hover': {
                          backgroundColor: '#FFF3E5',
                        },
                      },
                    }}
                  />
                </div>
              </div>
              {/* Location */}
              <div className="flex flex-col mb-4 location-field-container">
                <label
                  style={{
                    fontFamily: 'Nunito Sans, sans-serif',
                    fontSize: { xs: '10px', sm: '12px' },
                    fontWeight: '600',
                    color: '#202224',
                    marginBottom: '6px',
                  }}
                >
                  Location
                </label>
                <TextInput
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  placeholder="e.g., HQ - Third Floor"
                  styles={{
                    input: {
                      fontFamily: 'Nunito Sans, sans-serif',
                      fontSize: { xs: '10px', sm: '12px' },
                      color: '#595C60',
                      backgroundColor: '#FCFCFC',
                      border: '0.6px solid #595C60',
                      borderRadius: '6px',
                      width: '100%',
                      height: { xs: '36px', sm: '40px' },
                      '&:focus': {
                        borderColor: '#FF6B00',
                        outline: 'none',
                      },
                    },
                  }}
                />
              </div>

              {/* Divider */}
              <div
                style={{
                  borderTop: '1px solid #E0E0E0',
                  margin: '16px 0',
                }}
              />

              {/* System Operation Section */}
              <h2
                style={{
                  fontFamily: 'Nunito Sans, sans-serif',
                  fontSize: { xs: '18px', sm: '20px' },
                  fontWeight: 700,
                  color: '#202224',
                  marginBottom: '16px',
                }}
              >
                System Operation
              </h2>
              <div className="mb-4">
                {/* Operating System */}
                <div className="flex flex-col location-field-container">
                  <label
                    style={{
                      fontFamily: 'Nunito Sans, sans-serif',
                      fontSize: { xs: '10px', sm: '12px' },
                      fontWeight: 600,
                      color: '#202224',
                      marginBottom: '6px',
                    }}
                  >
                    Operating System*
                  </label>
                  <TextInput
                    value={formData.os}
                    readOnly
                    placeholder="Fetched OS would show up here"
                    styles={{
                      input: {
                        fontFamily: 'Nunito Sans, sans-serif',
                        fontSize: { xs: '10px', sm: '12px' },
                        color: '#595C60',
                        backgroundColor: '#EDEDED',
                        border: '0.6px solid #595C60',
                        borderRadius: '6px',
                        width: '100%',
                        height: { xs: '36px', sm: '40px' },
                        display: 'flex',
                        alignItems: 'center',
                        paddingRight: '30px',
                        boxSizing: 'border-box',
                        '&:focus': {
                          borderColor: '#FF6B00',
                          outline: 'none',
                        },
                      },
                      root: {
                        width: '100%',
                      },
                    }}
                    rightSection={
                      formData.os && (
                        <FontAwesomeIcon
                          icon={
                            formData.os.toLowerCase().includes('windows')
                              ? faWindowsBrand
                              : faLinuxBrand
                          }
                          style={{
                            color: formData.os.toLowerCase().includes('windows')
                              ? '#00B0FF'
                              : '#202224',
                            marginRight: '10px',
                          }}
                        />
                      )
                    }
                  />
                  <Button
                    className="export-button fetch-os-button"
                    styles={{
                      root: {
                        width: { xs: '100px !important', sm: '120px !important' },
                        minWidth: { xs: '100px !important', sm: '120px !important' },
                        maxWidth: { xs: '100px !important', sm: '120px !important' },
                        height: { xs: '32px', sm: '36px' },
                        backgroundColor: '#FF7A00',
                        borderRadius: '20px',
                        padding: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: 'none',
                        cursor: 'pointer',
                        boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
                        marginTop: '8px',
                        alignSelf: { xs: 'flex-start', sm: 'flex-start' },
                        '&:hover': {
                          backgroundColor: '#FF7A00',
                        },
                      },
                      label: {
                        fontFamily: 'Nunito Sans, sans-serif',
                        fontSize: { xs: '12px', sm: '14px' },
                        fontWeight: 500,
                        color: '#fff',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                    }}
                    onClick={(e) => {
                      handleRippleEffect(e);
                      handleFetchOs();
                    }}
                    disabled={fetchingOs}
                  >
                    {fetchingOs ? 'Fetching...' : 'Fetch OS'}
                  </Button>
                  {osFetchStatus && (
                    <span
                      style={{
                        fontFamily: 'Nunito Sans, sans-serif',
                        fontSize: { xs: '8px', sm: '10px' },
                        color: osFetchStatus.includes('successfully') ? '#2ECC71' : '#E74C3C',
                        marginTop: '6px',
                      }}
                    >
                      {osFetchStatus}
                    </span>
                  )}
                </div>
              </div>

              {/* Divider */}
              <div
                style={{
                  borderTop: '1px solid #E0E0E0',
                  margin: '16px 0',
                }}
              />

              {/* Add Device Button */}
              <div className="flex justify-end">
                <Button
                  className="export-button"
                  styles={{
                    root: {
                      width: { xs: '100px', sm: '120px' },
                      height: { xs: '32px', sm: '36px' },
                      backgroundColor: '#FF7A00',
                      borderRadius: '20px',
                      padding: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: 'none',
                      cursor: 'pointer',
                      boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
                      '&:hover': {
                        backgroundColor: '#FF7A00',
                      },
                    },
                    label: {
                      fontFamily: 'Nunito Sans, sans-serif',
                      fontSize: { xs: '12px', sm: '14px' },
                      fontWeight: 500,
                      color: '#fff',
                    },
                  }}
                  onClick={(e) => {
                    handleRippleEffect(e);
                    handleAddDevice();
                  }}
                  disabled={!formData.deviceName || !formData.model || !formData.department || !formData.os}
                >
                  Add Device
                </Button>
              </div>
            </div>
          </Modal>
        </Container>
      </Box>

      {/* Ripple Effect and Custom Styles */}
      <style>
        {`
          .export-button {
            position: relative;
            overflow: hidden;
          }
          .export-button-ripple {
            position: absolute;
            border-radius: 50%;
            background-color: rgba(255, 255, 255, 0.4);
            transform: scale(0);
            animation: ripple 0.6s linear;
            pointer-events: none;
          }
          @keyframes ripple {
            to {
              transform: scale(4);
              opacity: 0;
            }
          }
          .fetch-os-button {
            width: 120px !important;
          }
          @media (max-width: 600px) {
            .fetch-os-button {
              width: 100px !important;
            }
          }
          .location-field-container {
            max-width: 100%;
          }
          @media (min-width: 600px) {
            .location-field-container {
              max-width: 270px !important;
            }
          }
        `}
      </style>
    </Box>
  );
}

export default Encryption;